import { useEffect, useMemo, useState } from 'react';
import { useMe } from '../me';
import { isLayerShown, nextLayerDefaults, type LayerChoices } from './calendar-layers';
import { readCalendarStore, writeCalendarStore } from './calendar-storage';
import type { CalendarData, CalendarLayer } from './use-calendar-items';

// Which layers are on — one rule for the Calendar screen and for every summary of it (a Space's card),
// so a count never includes a calendar the screen it opens leaves out. The rules are `calendar-layers.ts`.

export { isLayerShown, itemsOfShownLayers, type LayerChoices } from './calendar-layers';

const LAYER_CHOICES_STORE = 'layerChoices';
const LAYER_DEFAULTS_STORE = 'layerDefaults';

export function useShownLayers(data: Pick<CalendarData, 'layers' | 'settledLayers'>): {
  shown: Set<string>;
  toggle: (layer: CalendarLayer) => void;
  /** Drops the reader's own choices, so every layer is back to what its provider shows. */
  reset: () => void;
  hasChoices: boolean;
} {
  const userId = useMe()?.userId;
  const [choices, setChoices] = useState<LayerChoices>({});
  const [defaults, setDefaults] = useState<LayerChoices>({});
  // Whose stored defaults are in state; until they are, nothing new is written over them.
  const [loadedFor, setLoadedFor] = useState<string | undefined>(undefined);
  useEffect(() => {
    setChoices(readCalendarStore<LayerChoices>(LAYER_CHOICES_STORE, userId) ?? {});
    setDefaults(readCalendarStore<LayerChoices>(LAYER_DEFAULTS_STORE, userId) ?? {});
    setLoadedFor(userId);
  }, [userId]);
  useEffect(() => {
    if (!userId || loadedFor !== userId) return;
    const next = nextLayerDefaults(data.layers, defaults, data.settledLayers);
    if (!next) return;
    setDefaults(next);
    writeCalendarStore(LAYER_DEFAULTS_STORE, userId, next);
  }, [data.layers, data.settledLayers, defaults, userId, loadedFor]);

  const shown = useMemo(
    () => new Set(data.layers.filter((layer) => isLayerShown(layer, choices, defaults)).map((layer) => layer.id)),
    [data.layers, choices, defaults],
  );
  const toggle = (layer: CalendarLayer) => {
    setChoices((current) => {
      const next = { ...current, [layer.id]: !shown.has(layer.id) };
      writeCalendarStore(LAYER_CHOICES_STORE, userId, next);
      return next;
    });
  };
  const reset = () => {
    setChoices({});
    writeCalendarStore(LAYER_CHOICES_STORE, userId, {});
  };
  const hasChoices = data.layers.some((layer) => choices[layer.id] !== undefined);
  return { shown, toggle, reset, hasChoices };
}
